import React, { memo } from "react";
import { Store } from "redux";
import { useSelector } from "react-redux";
import { RaceCell, Icon } from "@tvg/design-system/web";
import { useURPProgramPageOnXSell } from "@urp/amplitude-experiments";
import buildRaceUrl from "@tvg/formatter/url";
import { Race, RaceStatusEnum } from "@tvg/ts-types/Race";
import {
  getActiveBets,
  getOpenTracks
} from "@urp/lib-racetracks/src/redux/selectors";
import { breakpoints, useMediaQuery } from "@tvg/design-system";
import { buildQaLabel } from "@tvg/test-utils/utils";
import { get, sortBy } from "lodash";
import {
  isTrackOpen,
  hasTrackPromoCPP,
  hasRacePromoCPP,
  hasRacePromoCPPOptedIn
} from "@urp/lib-racetracks/src/utils/races";
import { getCPPPromos, getIsLogged } from "@urp/store-selectors";
import { SpecialWagerTypesNamesEnum } from "@tvg/ts-types/SpecialWager";
import { isAdvancedSpecialPick } from "@urp/lib-racetracks/src/utils/trackInfo";
import Collapsible from "../Collapsible";
import CollapsibleSummary from "../CollapsibleSummary";
import { Props } from "./types";
import { LETTER_BLOCK, LIST, RACE } from "../../utils/constants";
import { ScrollButton, RaceListContainer } from "./styled-components";
import { useRaceScroll } from "../../hooks/useRaceScroll";

const CollapsibleWrapper = ({
  track,
  onTrackClickHandler,
  module,
  qaLabel = "",
  isResults = false,
  checkRacePromos,
  onRaceCellClick
}: Props) => {
  const openTracks = useSelector(getOpenTracks);
  const shouldUseUrpPPonXSell = useURPProgramPageOnXSell();
  const activeBets = useSelector((store: Store) =>
    getActiveBets(store, shouldUseUrpPPonXSell)
  );
  const races = track.races as Race[] | undefined;
  const isOpen = isTrackOpen(openTracks, track.code) && !!races;
  const isDesktop = useMediaQuery(breakpoints.tablet.min.sm);

  const specialWagerType: SpecialWagerTypesNamesEnum =
    !isResults &&
    (isAdvancedSpecialPick(track.specialCardTypes ?? [])
      ? SpecialWagerTypesNamesEnum.NON_CONSECUTIVE_PICK
      : get(track, "specialCardTypes[0].specialCardTypeName", ""));

  const isSpecialPick =
    specialWagerType === SpecialWagerTypesNamesEnum.NON_CONSECUTIVE_PICK;

  const {
    isNextEnabled,
    isPrevEnabled,
    containerRef: racesRef,
    handleScroll
  } = useRaceScroll({ races, isOpen, hasNavigationButtons: isDesktop });

  const cppPromos = useSelector(getCPPPromos);
  const hasPromoCPP = hasTrackPromoCPP(cppPromos, track);
  const isLogged = useSelector(getIsLogged);

  const renderRaceCell = (race: Race) => {
    const firstLegOfPicks = races?.find(({ number }) => number === "1");
    const isFirstLegOpen =
      firstLegOfPicks &&
      ![RaceStatusEnum.RACE_OFF, RaceStatusEnum.RACE_OFFICIAL].includes(
        firstLegOfPicks?.status.code
      );

    const specialPickUrl = buildRaceUrl(
      race.track.code,
      race.track.name,
      Number((isFirstLegOpen ? firstLegOfPicks.number : race.number) ?? 0)
    );

    const raceUrl = isSpecialPick
      ? specialPickUrl
      : buildRaceUrl(race.track.code, race.track.name, +race.number);

    const { hasPromoAvailable, isOptedInPromo } = checkRacePromos(race.promos);

    const hasCPPPromoAvailable = hasRacePromoCPP(cppPromos, race);
    const isCPPPromosOptedIn = hasRacePromoCPPOptedIn(
      cppPromos,
      race,
      isLogged
    );
    const RaceCellComponent = isSpecialPick
      ? RaceCell.SpecialPickLeg
      : RaceCell.Summary;

    return (
      <RaceCellComponent
        key={race.id}
        race={race}
        qaLabel={buildQaLabel([qaLabel, LETTER_BLOCK, race.id])}
        to={raceUrl}
        hasPromosAvailable={hasPromoAvailable || hasCPPPromoAvailable}
        isOptedIn={isOptedInPromo || isCPPPromosOptedIn}
        numberActiveBets={get(activeBets, race?.tvgRaceId ?? "", 0)}
        onClick={() => onRaceCellClick?.(race, track, module)}
        maxWidth="108px"
      />
    );
  };

  return (
    <Collapsible
      key={track.code}
      summary={
        <CollapsibleSummary
          hasPromo={track.hasAboveTheLinePromo || hasPromoCPP}
          qaLabel={qaLabel}
          module={module}
          track={track}
        />
      }
      isOpen={isOpen}
      onClick={() => onTrackClickHandler(track.code, isResults)}
      qaLabel={buildQaLabel([qaLabel, track.code])}
    >
      {isDesktop && (
        <ScrollButton
          data-qa-label={buildQaLabel([qaLabel, "prev", "button"])}
          onClick={() => handleScroll("prev")}
          disabled={!isPrevEnabled}
          hasMarginLeft
        >
          <Icon name="arrowLeft" size="s" />
        </ScrollButton>
      )}

      <RaceListContainer
        ref={racesRef}
        hasScrollButtons={isDesktop}
        data-qa-label={buildQaLabel([qaLabel, RACE, LIST])}
      >
        {isOpen &&
          (isSpecialPick ? sortBy(races, "number") : races).map((race: Race) =>
            renderRaceCell(race)
          )}
      </RaceListContainer>

      {isDesktop && (
        <ScrollButton
          data-qa-label={buildQaLabel([qaLabel, "next", "button"])}
          onClick={() => handleScroll("next")}
          disabled={!isNextEnabled}
          hasMarginRight
        >
          <Icon name="arrowRight" size="s" />
        </ScrollButton>
      )}
    </Collapsible>
  );
};

export default memo(CollapsibleWrapper);
